/**
 * DicomScrub UI. Vanilla TypeScript, no framework, no external requests.
 * Heavy lifting is delegated to src/worker/anonymize.worker.ts.
 */
import "./styles.css";
import { zipSync } from "fflate";
import { DEFAULT_OPTIONS, type BatchResult, type CustomRule, type FileSummary, type PreviewBitmap, type ProfileOptions, type RedactRect } from "./core/types";
import { auditCsv, mappingCsv, methodsStatement } from "./core/audit";
import { FREE_FILE_LIMIT, loadStoredToken, storeToken, verifyToken, type LicenseState } from "./core/license";

// ---------- Worker client ----------

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void; onProgress?: (done: number, total: number, name: string) => void };

class Engine {
  private worker = new Worker(new URL("./worker/anonymize.worker.ts", import.meta.url), { type: "module" });
  private seq = 0;
  private pending = new Map<number, Pending>();

  constructor() {
    this.worker.onmessage = (ev: MessageEvent) => {
      const m = ev.data;
      const p = this.pending.get(m.id);
      if (!p) return;
      switch (m.type) {
        case "progress": p.onProgress?.(m.done, m.total, m.name); break;
        case "inspected": this.pending.delete(m.id); p.resolve(m.summaries); break;
        case "preview": this.pending.delete(m.id); p.resolve(m.bitmap); break;
        case "processed": this.pending.delete(m.id); p.resolve(m.result); break;
        case "sample": this.pending.delete(m.id); p.resolve(m); break;
        case "error": this.pending.delete(m.id); p.reject(new Error(m.message)); break;
      }
    };
    this.worker.onerror = (e) => {
      for (const [id, p] of this.pending) { p.reject(new Error(e.message)); this.pending.delete(id); }
    };
  }

  private call<T>(msg: Record<string, unknown>, transfer: Transferable[] = [], onProgress?: Pending["onProgress"]): Promise<T> {
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, onProgress });
      this.worker.postMessage({ ...msg, id }, transfer);
    });
  }

  async inspect(entries: Entry[]): Promise<FileSummary[]> {
    const files = await Promise.all(entries.map(async (e) => ({ index: e.index, name: e.name, buffer: await e.file.arrayBuffer() })));
    return this.call<FileSummary[]>({ type: "inspect", files }, files.map((f) => f.buffer));
  }

  async preview(entry: Entry): Promise<PreviewBitmap | null> {
    const buffer = await entry.file.arrayBuffer();
    return this.call<PreviewBitmap | null>({ type: "preview", index: entry.index, buffer }, [buffer]);
  }

  async process(entries: Entry[], options: ProfileOptions, onProgress: Pending["onProgress"]): Promise<BatchResult> {
    const files = await Promise.all(entries.map(async (e) => ({ index: e.index, name: e.name, buffer: await e.file.arrayBuffer() })));
    return this.call<BatchResult>({ type: "process", options, files }, files.map((f) => f.buffer), onProgress);
  }

  sample(): Promise<{ name: string; buffer: ArrayBuffer }> {
    return this.call({ type: "sample" });
  }
}

// ---------- State ----------

interface Entry {
  index: number;
  name: string;
  file: File;
  summary?: FileSummary;
}

const engine = new Engine();
const state = {
  entries: [] as Entry[],
  nextIndex: 0,
  selected: null as number | null,
  redactions: {} as Record<number, RedactRect[]>,
  preview: null as PreviewBitmap | null,
  result: null as BatchResult | null,
  license: { valid: false } as LicenseState,
  redactMode: false,
  busy: false,
};

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const els = {
  dropzone: $("dropzone"),
  fileInput: $<HTMLInputElement>("fileInput"),
  folderInput: $<HTMLInputElement>("folderInput"),
  pickFiles: $("pickFiles"),
  pickFolder: $("pickFolder"),
  loadSample: $("loadSample"),
  clearFiles: $("clearFiles"),
  fileSummary: $("fileSummary"),
  fileCount: $("fileCount"),
  fileTable: $<HTMLTableElement>("fileTable"),
  limitBox: $("limitBox"),
  options: $("options"),
  dateShift: $<HTMLInputElement>("dateShift"),
  prefix: $<HTMLInputElement>("prefix"),
  startAt: $<HTMLInputElement>("startAt"),
  customRules: $<HTMLTextAreaElement>("customRules"),
  previewEmpty: $("previewEmpty"),
  previewCanvas: $<HTMLCanvasElement>("previewCanvas"),
  previewInfo: $("previewInfo"),
  redactMode: $<HTMLButtonElement>("redactMode"),
  clearRects: $<HTMLButtonElement>("clearRects"),
  rectList: $("rectList"),
  runBtn: $<HTMLButtonElement>("runBtn"),
  progressBar: $("progressBar"),
  status: $("status"),
  errBox: $("errBox"),
  results: $("results"),
  resultSummary: $("resultSummary"),
  dlZip: $<HTMLButtonElement>("dlZip"),
  dlAudit: $<HTMLButtonElement>("dlAudit"),
  dlMethods: $<HTMLButtonElement>("dlMethods"),
  dlMapping: $<HTMLButtonElement>("dlMapping"),
  auditView: $("auditView"),
  licenseBtn: $("licenseBtn"),
  planBadge: $("planBadge"),
  licenseModal: $("licenseModal"),
  licenseStatus: $("licenseStatus"),
  licenseInput: $<HTMLTextAreaElement>("licenseInput"),
  licenseSave: $("licenseSave"),
  licenseClose: $("licenseClose"),
  licenseRemove: $("licenseRemove"),
  netBadge: $("netBadge"),
  year: $("year"),
};

// ---------- Helpers ----------

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}

function download(name: string, data: BlobPart, type: string): void {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function isPro(): boolean {
  return state.license.valid;
}

function setStatus(text: string): void {
  els.status.textContent = text;
}

function showError(msg: string | null): void {
  els.errBox.hidden = !msg;
  els.errBox.textContent = msg ?? "";
}

// ---------- File intake ----------

async function collectFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const out: File[] = [];
  const items = Array.from(dt.items ?? []);
  const entries = items
    .map((it) => (typeof it.webkitGetAsEntry === "function" ? it.webkitGetAsEntry() : null))
    .filter(Boolean) as FileSystemEntry[];
  if (entries.length === 0) return Array.from(dt.files);

  const readDir = (dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => {
      const reader = dir.createReader();
      const all: FileSystemEntry[] = [];
      const step = () => reader.readEntries((batch) => {
        if (batch.length === 0) resolve(all);
        else { all.push(...batch); step(); }
      }, reject);
      step();
    });
  const readFile = (fe: FileSystemFileEntry): Promise<File> => new Promise((resolve, reject) => fe.file(resolve, reject));

  const walk = async (e: FileSystemEntry, path: string): Promise<void> => {
    if (e.isFile) {
      const f = await readFile(e as FileSystemFileEntry);
      Object.defineProperty(f, "relPath", { value: path + f.name });
      out.push(f);
    } else if (e.isDirectory) {
      for (const child of await readDir(e as FileSystemDirectoryEntry)) await walk(child, path + e.name + "/");
    }
  };
  for (const e of entries) await walk(e, "");
  return out;
}

function shouldSkip(f: File): boolean {
  const n = f.name.toUpperCase();
  return n === "DICOMDIR" || n.endsWith(".TXT") || n.endsWith(".PDF") || n.endsWith(".JPG") || n.endsWith(".PNG") || n.endsWith(".XML") || n.endsWith(".JSON") || f.size < 132;
}

async function addFiles(files: File[]): Promise<void> {
  const fresh: Entry[] = [];
  for (const f of files) {
    if (shouldSkip(f)) continue;
    const name = (f as File & { relPath?: string }).relPath ?? ((f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name);
    fresh.push({ index: state.nextIndex++, name, file: f });
  }
  if (fresh.length === 0) return;
  state.entries.push(...fresh);
  state.result = null;
  els.results.classList.remove("show");
  renderTable();
  setStatus(`Reading ${fresh.length} file(s)…`);
  try {
    // Inspect in chunks so memory stays bounded on big folders.
    const CHUNK = 50;
    for (let i = 0; i < fresh.length; i += CHUNK) {
      const slice = fresh.slice(i, i + CHUNK);
      const summaries = await engine.inspect(slice);
      for (const s of summaries) {
        const e = state.entries.find((x) => x.index === s.index);
        if (e) e.summary = s;
      }
      renderTable();
    }
    setStatus("Ready");
  } catch (e) {
    showError((e as Error).message);
  }
  updateRunState();
}

function renderTable(): void {
  const tbody = els.fileTable.tBodies[0];
  const rows = state.entries.map((e, i) => {
    const s = e.summary;
    const status = !s ? "reading…" : s.ok ? (s.previewable ? "OK · preview" : "OK") : `<span class="err">${esc(s.error ?? "error")}</span>`;
    const cls = e.index === state.selected ? "selected" : "";
    return `<tr data-index="${e.index}" class="${cls}"><td>${i + 1}</td><td title="${esc(e.name)}">${esc(e.name)}</td><td>${esc(s?.modality ?? "")}</td><td>${esc(s?.patientName ?? "")}${s?.patientId ? ` <span class="hint">(${esc(s.patientId)})</span>` : ""}</td><td>${esc(s?.studyDate ?? "")}</td><td>${fmtSize(e.file.size)}</td><td>${status}</td></tr>`;
  });
  tbody.innerHTML = rows.join("");
  els.fileTable.hidden = state.entries.length === 0;
  els.fileSummary.hidden = state.entries.length === 0;
  const ok = state.entries.filter((e) => e.summary?.ok).length;
  const bad = state.entries.filter((e) => e.summary && !e.summary.ok).length;
  const total = state.entries.reduce((a, e) => a + e.file.size, 0);
  els.fileCount.textContent = `${state.entries.length} file(s), ${fmtSize(total)} · ${ok} DICOM · ${bad} skipped`;
  const over = !isPro() && ok > FREE_FILE_LIMIT;
  els.limitBox.hidden = !over;
  els.limitBox.innerHTML = over
    ? `Free tier processes <strong>${FREE_FILE_LIMIT} files per batch</strong>. You added ${ok}. Only the first ${FREE_FILE_LIMIT} will be anonymized. <a href="/pricing.html">Upgrade to Pro</a> for unlimited batches, redaction and custom rules.`
    : "";
}

function updateRunState(): void {
  const ok = state.entries.filter((e) => e.summary?.ok).length;
  els.runBtn.disabled = state.busy || ok === 0;
  els.redactMode.disabled = !isPro() || !state.preview;
  els.clearRects.disabled = !isPro() || state.selected === null || !(state.redactions[state.selected]?.length);
  els.customRules.disabled = !isPro();
  els.dlMapping.disabled = !isPro();
}

// ---------- Preview ----------

async function selectFile(index: number): Promise<void> {
  state.selected = index;
  state.preview = null;
  renderTable();
  renderAuditForSelected();
  const e = state.entries.find((x) => x.index === index);
  if (!e || !e.summary?.ok) { showPreview(null, "Not a DICOM file."); updateRunState(); return; }
  if (!e.summary.previewable) {
    showPreview(null, `No preview: ${e.summary.transferSyntax?.startsWith("1.2.840.10008.1.2.4") ? "compressed transfer syntax" : "unsupported pixel format"} (${e.summary.transferSyntax}). The file will still be anonymized.`);
    updateRunState();
    return;
  }
  els.previewEmpty.textContent = "Rendering…";
  try {
    const bmp = await engine.preview(e);
    if (state.selected !== index) return;
    state.preview = bmp;
    showPreview(bmp, bmp ? "" : "Could not decode pixel data.");
    els.previewInfo.textContent = bmp ? `${e.summary.modality ?? ""} ${bmp.width}×${bmp.height}${(e.summary.frames ?? 1) > 1 ? ` · ${e.summary.frames} frames` : ""}` : "";
  } catch (err) {
    showPreview(null, (err as Error).message);
  }
  updateRunState();
  renderRects();
}

function showPreview(bmp: PreviewBitmap | null, message: string): void {
  const c = els.previewCanvas;
  if (!bmp) {
    c.hidden = true;
    els.previewEmpty.hidden = false;
    els.previewEmpty.textContent = message || "Select a file to preview.";
    els.previewInfo.textContent = "";
    return;
  }
  c.hidden = false;
  els.previewEmpty.hidden = true;
  c.width = bmp.width;
  c.height = bmp.height;
  drawPreview();
}

function drawPreview(): void {
  const bmp = state.preview;
  const c = els.previewCanvas;
  if (!bmp) return;
  const ctx = c.getContext("2d")!;
  const img = new ImageData(bmp.rgba as unknown as Uint8ClampedArray<ArrayBuffer>, bmp.width, bmp.height);
  ctx.putImageData(img, 0, 0);
  const rects = state.selected !== null ? state.redactions[state.selected] ?? [] : [];
  ctx.fillStyle = "rgba(248,113,113,0.45)";
  ctx.strokeStyle = "#f87171";
  ctx.lineWidth = Math.max(1, bmp.width / 400);
  for (const r of rects) { ctx.fillRect(r.x, r.y, r.w, r.h); ctx.strokeRect(r.x, r.y, r.w, r.h); }
  if (drag) {
    ctx.strokeStyle = "#fbbf24";
    ctx.strokeRect(drag.x, drag.y, drag.w, drag.h);
  }
}

function renderRects(): void {
  const rects = state.selected !== null ? state.redactions[state.selected] ?? [] : [];
  els.rectList.innerHTML = rects.map((r, i) => `<span class="rect-chip">${Math.round(r.x)},${Math.round(r.y)} ${Math.round(r.w)}×${Math.round(r.h)}<button type="button" data-rm="${i}" aria-label="Remove box">×</button></span>`).join("");
  updateRunState();
}

let drag: RedactRect | null = null;
let dragStart: { x: number; y: number } | null = null;

function canvasPoint(ev: PointerEvent): { x: number; y: number } {
  const c = els.previewCanvas;
  const r = c.getBoundingClientRect();
  return { x: ((ev.clientX - r.left) / r.width) * c.width, y: ((ev.clientY - r.top) / r.height) * c.height };
}

els.previewCanvas.addEventListener("pointerdown", (ev) => {
  if (!state.redactMode || !state.preview || !isPro()) return;
  els.previewCanvas.setPointerCapture(ev.pointerId);
  dragStart = canvasPoint(ev);
  drag = { ...dragStart, w: 0, h: 0 };
});
els.previewCanvas.addEventListener("pointermove", (ev) => {
  if (!drag || !dragStart) return;
  const p = canvasPoint(ev);
  drag = { x: Math.min(p.x, dragStart.x), y: Math.min(p.y, dragStart.y), w: Math.abs(p.x - dragStart.x), h: Math.abs(p.y - dragStart.y) };
  drawPreview();
});
els.previewCanvas.addEventListener("pointerup", () => {
  if (drag && state.selected !== null && drag.w > 2 && drag.h > 2) {
    (state.redactions[state.selected] ??= []).push(drag);
  }
  drag = null;
  dragStart = null;
  drawPreview();
  renderRects();
});
els.rectList.addEventListener("click", (ev) => {
  const btn = (ev.target as HTMLElement).closest("button[data-rm]") as HTMLButtonElement | null;
  if (!btn || state.selected === null) return;
  state.redactions[state.selected]?.splice(Number(btn.dataset.rm), 1);
  drawPreview();
  renderRects();
});
els.redactMode.addEventListener("click", () => {
  state.redactMode = !state.redactMode;
  els.redactMode.textContent = state.redactMode ? "Drawing… (click to stop)" : "Draw redaction box";
  els.redactMode.classList.toggle("primary", state.redactMode);
  els.previewCanvas.style.cursor = state.redactMode ? "crosshair" : "default";
});
els.clearRects.addEventListener("click", () => {
  if (state.selected !== null) delete state.redactions[state.selected];
  drawPreview();
  renderRects();
});

// ---------- Options ----------

function parseRules(text: string): CustomRule[] {
  const rules: CustomRule[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const m = /^\(?([0-9a-fA-F]{4})[, ]?([0-9a-fA-F]{4})\)?\s+(keep|remove|empty|replace)(?:\s+(.*))?$/.exec(line);
    if (!m) continue;
    rules.push({ tag: (m[1] + m[2]).toUpperCase(), action: m[3] as CustomRule["action"], value: m[4]?.trim() });
  }
  return rules;
}

function gatherOptions(): ProfileOptions {
  const o: ProfileOptions = { ...DEFAULT_OPTIONS, customRules: [], redactions: {} };
  els.options.querySelectorAll<HTMLInputElement>("input[data-opt]").forEach((cb) => {
    (o as unknown as Record<string, boolean>)[cb.dataset.opt!] = cb.checked;
  });
  const shift = parseInt(els.dateShift.value, 10);
  o.dateShiftDays = isNaN(shift) ? 0 : shift;
  o.patientIdPrefix = (els.prefix.value || "ANON").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 12) || "ANON";
  o.patientIdStart = Math.max(0, parseInt(els.startAt.value, 10) || 1);
  if (isPro()) {
    o.customRules = parseRules(els.customRules.value);
    o.redactions = JSON.parse(JSON.stringify(state.redactions));
  }
  return o;
}

// ---------- Run ----------

async function run(): Promise<void> {
  if (state.busy) return;
  showError(null);
  let entries = state.entries.filter((e) => e.summary?.ok);
  if (!isPro() && entries.length > FREE_FILE_LIMIT) entries = entries.slice(0, FREE_FILE_LIMIT);
  if (entries.length === 0) return;
  state.busy = true;
  state.result = null;
  els.results.classList.remove("show");
  updateRunState();
  els.progressBar.style.width = "0%";
  setStatus("Starting…");
  const options = gatherOptions();
  try {
    const result = await engine.process(entries, options, (done, total, name) => {
      els.progressBar.style.width = `${Math.round((done / total) * 100)}%`;
      setStatus(done < total ? `${done + 1}/${total} ${name}` : `Done: ${total} files`);
    });
    state.result = result;
    renderResult(result);
  } catch (e) {
    showError((e as Error).message);
    setStatus("Failed");
  } finally {
    state.busy = false;
    updateRunState();
  }
}

function renderResult(r: BatchResult): void {
  const ok = r.files.filter((f) => f.ok);
  const bad = r.files.filter((f) => !f.ok);
  const changes = ok.reduce((a, f) => a + f.audit.length, 0);
  const patients = Object.keys(r.patientMap).length;
  els.resultSummary.innerHTML = `<strong>${ok.length}</strong> file(s) anonymized · <strong>${patients}</strong> patient(s) → ${esc(r.options.patientIdPrefix)}nnn · <strong>${Object.keys(r.uidMap).length}</strong> UIDs remapped · <strong>${changes}</strong> attribute changes · date shift: ${r.options.modifyDates ? `${r.dateShiftDays > 0 ? "+" : ""}${r.dateShiftDays} days` : "dates removed"}${bad.length ? ` · <span class="err">${bad.length} failed</span>` : ""}`;
  els.results.classList.add("show");
  renderAuditForSelected();
  if (state.selected === null && ok.length) selectFile(ok[0].index);
}

function renderAuditForSelected(): void {
  const r = state.result;
  if (!r || state.selected === null) { els.auditView.innerHTML = ""; return; }
  const f = r.files.find((x) => x.index === state.selected);
  if (!f) { els.auditView.innerHTML = `<div class="hint">This file was not part of the last run.</div>`; return; }
  if (!f.ok) { els.auditView.innerHTML = `<div class="X">ERROR: ${esc(f.error ?? "")}</div>`; return; }
  els.auditView.innerHTML = f.audit.map((a) => `<div><span class="${a.action}">${a.action.padEnd(6)}</span> ${esc(a.tag)} ${esc(a.name)}: ${esc(a.before)}${a.after ? ` → ${esc(a.after)}` : ""}</div>`).join("") || `<div class="hint">No changes.</div>`;
}

function buildZip(r: BatchResult): Uint8Array {
  const files: Record<string, Uint8Array> = {};
  for (const f of r.files) if (f.ok && f.bytes) files[f.outputName] = new Uint8Array(f.bytes);
  files["README.txt"] = new TextEncoder().encode(methodsStatement(r) + "\n\nGenerated by DicomScrub (https://dicomscrub.com). No data left your computer.\n");
  return zipSync(files, { level: 0 });
}

els.dlZip.addEventListener("click", () => { if (state.result) download("dicomscrub-anonymized.zip", buildZip(state.result) as unknown as BlobPart, "application/zip"); });
els.dlAudit.addEventListener("click", () => { if (state.result) download("dicomscrub-audit.csv", auditCsv(state.result), "text/csv"); });
els.dlMethods.addEventListener("click", () => { if (state.result) download("dicomscrub-methods.txt", methodsStatement(state.result), "text/plain"); });
els.dlMapping.addEventListener("click", () => {
  if (!isPro()) { openLicense(); return; }
  if (state.result) download("dicomscrub-mapping-CONFIDENTIAL.csv", mappingCsv(state.result), "text/csv");
});

// ---------- License ----------

function openLicense(): void {
  els.licenseInput.value = loadStoredToken() ?? "";
  els.licenseStatus.textContent = state.license.valid
    ? `Active: Pro for ${state.license.payload?.sub ?? "?"}${state.license.payload?.exp ? `, valid until ${new Date(state.license.payload.exp * 1000).toLocaleDateString()}` : " (perpetual)"}`
    : (state.license.reason ? `Not active: ${state.license.reason}` : "Not active (free tier).");
  els.licenseModal.classList.add("show");
}

async function applyLicense(token: string | null): Promise<void> {
  state.license = token ? await verifyToken(token) : { valid: false };
  els.planBadge.textContent = state.license.valid ? "Pro" : "Free";
  els.planBadge.className = `badge ${state.license.valid ? "pro" : "free"}`;
  renderTable();
  updateRunState();
}

els.licenseBtn.addEventListener("click", openLicense);
els.licenseClose.addEventListener("click", () => els.licenseModal.classList.remove("show"));
els.licenseModal.addEventListener("click", (ev) => { if (ev.target === els.licenseModal) els.licenseModal.classList.remove("show"); });
els.licenseSave.addEventListener("click", async () => {
  const token = els.licenseInput.value.trim();
  await applyLicense(token);
  if (state.license.valid) { storeToken(token); els.licenseModal.classList.remove("show"); }
  else els.licenseStatus.textContent = `Not active: ${state.license.reason ?? "invalid token"}`;
});
els.licenseRemove.addEventListener("click", async () => { storeToken(null); await applyLicense(null); els.licenseModal.classList.remove("show"); });

// ---------- Wiring ----------

els.pickFiles.addEventListener("click", () => els.fileInput.click());
els.pickFolder.addEventListener("click", () => els.folderInput.click());
els.fileInput.addEventListener("change", () => { addFiles(Array.from(els.fileInput.files ?? [])); els.fileInput.value = ""; });
els.folderInput.addEventListener("change", () => { addFiles(Array.from(els.folderInput.files ?? [])); els.folderInput.value = ""; });
els.dropzone.addEventListener("click", (ev) => { if ((ev.target as HTMLElement).tagName !== "BUTTON") els.fileInput.click(); });
els.dropzone.addEventListener("keydown", (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); els.fileInput.click(); } });
for (const evName of ["dragenter", "dragover"]) {
  els.dropzone.addEventListener(evName, (ev) => { ev.preventDefault(); els.dropzone.classList.add("over"); });
}
els.dropzone.addEventListener("dragleave", () => els.dropzone.classList.remove("over"));
els.dropzone.addEventListener("drop", async (ev) => {
  ev.preventDefault();
  els.dropzone.classList.remove("over");
  if (ev.dataTransfer) addFiles(await collectFromDataTransfer(ev.dataTransfer));
});
document.addEventListener("dragover", (ev) => ev.preventDefault());
document.addEventListener("drop", (ev) => ev.preventDefault());

els.clearFiles.addEventListener("click", () => {
  state.entries = [];
  state.selected = null;
  state.preview = null;
  state.redactions = {};
  state.result = null;
  els.results.classList.remove("show");
  showPreview(null, "Select a file to preview.");
  renderTable();
  renderRects();
  setStatus("Waiting for files");
  els.progressBar.style.width = "0%";
  updateRunState();
});

els.fileTable.addEventListener("click", (ev) => {
  const tr = (ev.target as HTMLElement).closest("tr[data-index]") as HTMLTableRowElement | null;
  if (tr) selectFile(Number(tr.dataset.index));
});

els.runBtn.addEventListener("click", run);

els.loadSample.addEventListener("click", async () => {
  try {
    const s = await engine.sample();
    addFiles([new File([s.buffer], s.name, { type: "application/dicom" })]);
  } catch (e) {
    showError((e as Error).message);
  }
});

// Network-isolation self-check: list every resource the page has loaded and confirm all are same-origin.
function networkCheck(): void {
  try {
    const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    const foreign = entries.filter((e) => { try { return new URL(e.name).origin !== location.origin; } catch { return false; } });
    if (foreign.length === 0) {
      els.netBadge.textContent = `${entries.length + 1} resources loaded, all from ${location.host}, 0 third-party`;
      els.netBadge.className = "badge ok";
    } else {
      els.netBadge.textContent = `Warning: ${foreign.length} third-party resource(s)`;
      els.netBadge.className = "badge";
    }
  } catch {
    els.netBadge.textContent = "Network check unavailable";
  }
}

els.year.textContent = String(new Date().getFullYear());
document.getElementById("commit")!.textContent = __COMMIT__;
applyLicense(loadStoredToken());
updateRunState();
window.addEventListener("load", () => setTimeout(networkCheck, 800));
